import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserSlice } from '../../context/store/store';
import {
    getNotifications,
    markAllNotificationsAsRead,
    markNotificationAsRead
} from '../../helpers/axiosHelper';
import { timeFormatter } from '../../helpers/timeZoneHelper';
import {
    disablePushNotifications,
    enablePushNotifications,
    syncExistingPushNotifications
} from '../../helpers/pushNotifications';
import './AdminNotifications.css';

const POLL_INTERVAL_MS = 20000;

const AdminNotifications = ({ headers }) => {
    const navigator = useNavigate();
    const authorization = headers?.Authorization;
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [markingAll, setMarkingAll] = useState(false);
    const [pushState, setPushState] = useState('checking');
    const [changingPush, setChangingPush] = useState(false);
    const requestRef = useRef(0);

    const load = useCallback(async () => {
        if (!authorization) return;
        const requestId = ++requestRef.current;
        try {
            const response = await getNotifications({ headers });
            if (requestId !== requestRef.current || getUserSlice.getState().headers.Authorization !== authorization) return;
            const rows = Array.isArray(response?.data)
                ? response.data.filter((row) => row?.audience === 'admin' && typeof row?._id === 'string')
                : [];
            setNotifications(rows);
        } catch (error) {
            // The queue itself remains available in Accounts. A temporary
            // notification failure must not interrupt administrative work.
        }
    }, [authorization, headers]);

    useEffect(() => {
        setNotifications([]);
        setMarkingAll(false);
        setOpen(false);
        if (!authorization) return undefined;
        load();
        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible') load();
        }, POLL_INTERVAL_MS);
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', onFocus);
            requestRef.current += 1;
        };
    }, [authorization, load]);

    useEffect(() => {
        let active = true;
        if (!authorization) {
            setPushState('checking');
            return () => { active = false; };
        }
        setPushState('checking');
        syncExistingPushNotifications({ headers })
            .then((state) => { if (active) setPushState(state); })
            .catch(() => { if (active) setPushState('error'); });
        return () => { active = false; };
    }, [authorization, headers]);

    const unread = useMemo(() => notifications.filter((row) => !row.readAt).length, [notifications]);

    const readAll = async () => {
        if (!unread || markingAll) return;
        requestRef.current += 1;
        const readAt = new Date().toISOString();
        setMarkingAll(true);
        setNotifications((rows) => rows.map((row) => (
            row.readAt ? row : { ...row, readAt }
        )));
        try {
            await markAllNotificationsAsRead({ headers });
        } catch (error) {
            await load();
        } finally {
            setMarkingAll(false);
        }
    };

    const openQueue = async (notification) => {
        setNotifications((rows) => rows.map((row) => (
            row._id === notification._id ? { ...row, readAt: row.readAt || new Date().toISOString() } : row
        )));
        try {
            await markNotificationAsRead({ id: notification._id, headers });
        } catch (error) {
            load();
        }
        setOpen(false);
        navigator('/accounts', { state: { notificationId: notification._id } });
    };

    const changePush = async () => {
        if (changingPush) return;
        setChangingPush(true);
        try {
            const state = pushState === 'enabled'
                ? await disablePushNotifications({ headers })
                : await enablePushNotifications({ headers });
            setPushState(state);
        } catch (error) {
            setPushState('error');
        } finally {
            setChangingPush(false);
        }
    };

    const pushMessage = {
        checking: 'Comprobando este dispositivo…',
        enabled: 'Recibirás avisos aunque el panel no esté abierto.',
        available: 'Actívalos para enterarte de nuevos pedidos por atender.',
        denied: 'El navegador bloqueó los avisos. Habilítalos desde la configuración del sitio.',
        'ios-install-required': 'En iPhone o iPad, agrega Gold Admin a la pantalla de inicio para activar los avisos.',
        unsupported: 'Este navegador no admite notificaciones push.',
        unavailable: 'Las notificaciones push no están disponibles temporalmente.',
        error: 'No pudimos configurar los avisos. Intenta nuevamente.'
    }[pushState] || 'Configura los avisos de este dispositivo.';

    if (!authorization) return null;

    return (
        <div className="adminNotifications">
            <button
                type="button"
                className="adminNotificationsTrigger"
                aria-label={unread ? `${unread} pedidos pendientes sin revisar` : 'Notificaciones de entrega'}
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
            >
                <i className="bi bi-bell" aria-hidden="true"></i>
                {unread > 0 && <span>{unread > 9 ? '9+' : unread}</span>}
            </button>

            {open && (
                <div className="adminNotificationsPanel">
                    <header>
                        <div>
                            <span>Cola de entrega</span>
                            <strong>Pedidos por atender</strong>
                        </div>
                        <button type="button" aria-label="Cerrar notificaciones" onClick={() => setOpen(false)}>×</button>
                    </header>
                    <section className={`adminPushPreference ${pushState === 'enabled' ? 'enabled' : ''}`}>
                        <div>
                            <strong>{pushState === 'enabled' ? 'Avisos push activos' : 'Avisos en este dispositivo'}</strong>
                            <span>{pushMessage}</span>
                        </div>
                        {['available', 'enabled', 'error'].includes(pushState) && (
                            <button type="button" disabled={changingPush} onClick={changePush}>
                                {changingPush
                                    ? 'Procesando…'
                                    : pushState === 'enabled' ? 'Desactivar' : 'Activar'}
                            </button>
                        )}
                    </section>
                    {notifications.length > 0 && (
                        <section className="adminNotificationsToolbar">
                            <span aria-live="polite">{unread ? `${unread} sin revisar` : 'Todo al día'}</span>
                            <button type="button" disabled={!unread || markingAll} onClick={readAll}>
                                {markingAll ? 'Marcando…' : unread ? 'Marcar todas como leídas' : 'Todas leídas'}
                            </button>
                        </section>
                    )}
                    <div>
                        {notifications.length === 0
                            ? <p>No hay novedades pendientes.</p>
                            : notifications.slice(0, 15).map((notification) => (
                                <button
                                    type="button"
                                    className={notification.readAt ? 'read' : 'unread'}
                                    onClick={() => openQueue(notification)}
                                    key={notification._id}
                                >
                                    <span>{notification.title}</span>
                                    <strong>{notification.message}</strong>
                                    <small className="adminNotificationTime">{notification.createdAt ? timeFormatter(notification.createdAt) : 'Reciente'}</small>
                                    <small>Ver cola de entrega →</small>
                                </button>
                            ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminNotifications;
